import type { KeywordRecommendationAction } from '@/types/keywordRecommendations';

const ASIN_LIKE_PATTERN = /^b0[a-z0-9]{8}$/i;

export type RecommendationSortField = 'cost' | 'clicks' | 'roas' | 'sales30d';
export type RecommendationSortOrder = 'asc' | 'desc';

export function isAsinLikeTerm(term: string): boolean {
  return ASIN_LIKE_PATTERN.test(term.trim());
}

export function stripDuplicateRoasFromReason(reason: string, roas: number): string {
  if (!reason) return '';
  const roasText = roas.toFixed(2);
  let cleaned = reason
    .replace(new RegExp(`\\bROAS\\s+${roasText}\\b`, 'gi'), '')
    .replace(/;\s*not yet a keyword in ad group\.?/gi, '')
    .replace(/Search term converted with\s*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  cleaned = cleaned.replace(/^[;,.\s]+|[;,.\s]+$/g, '').trim();
  return cleaned || 'Recommended based on period performance.';
}

export function filterBySearch<T>(
  rows: T[],
  query: string,
  fieldsFor: (row: T) => string[]
): T[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return rows;
  return rows.filter((row) =>
    fieldsFor(row).some((field) => field.toLowerCase().includes(normalized))
  );
}

export function filterByAction<T>(
  rows: T[],
  action: KeywordRecommendationAction | 'ALL',
  actionFor: (row: T) => KeywordRecommendationAction
): T[] {
  if (action === 'ALL') return rows;
  return rows.filter((row) => actionFor(row) === action);
}

export function sortByMetric<T>(
  rows: T[],
  sortBy: RecommendationSortField,
  sortOrder: RecommendationSortOrder,
  metricsFor: (row: T) => { cost: number; clicks: number; roas: number; sales30d: number }
): T[] {
  const sorted = [...rows].sort((a, b) => {
    const ma = metricsFor(a);
    const mb = metricsFor(b);
    const av = ma[sortBy] ?? 0;
    const bv = mb[sortBy] ?? 0;
    return av - bv;
  });
  return sortOrder === 'desc' ? sorted.reverse() : sorted;
}

export function actionTypeLabel(action: KeywordRecommendationAction): string {
  switch (action) {
    case 'ADD_KEYWORD':
      return 'Add keyword';
    case 'ADD_NEGATIVE_KEYWORD':
      return 'Add negative';
    case 'INCREASE_BID':
      return 'Increase bid';
    case 'DECREASE_BID':
      return 'Decrease bid';
    case 'PAUSE':
      return 'Pause';
    default:
      return action;
  }
}
